import { IJobHOCState } from "redux/_core/job/types";
import { ILocalRange } from "../vessel/onboard/types";
import { TEMFUsageData } from "../emf/@types";

export interface IVessel {
	_id: string;
	callSign: string;
	imoNumber: string;
	vesselName: string;
	vesselPrefix: string;
	vesselSubType: string;
	vesselType: string;
}

export type TBFL1UsageData = {
	_id: string;
	imoNumber: string;
	vesselName: string;
	bfl1RecordCount: number;
};
export type TBFL2UsageData = {
	_id: string;
	imoNumber: string;
	vesselName: string;
	bfl2RecordCount: number;
};

export enum EORSAggregateRecord {
	INCINERATOR = "INCINERATOR",
	SHORE_DISPOSAL = "SHORE DISPOSAL",
	WATER_EVAPORATION = "WATER EVAPORATION",
	SLOP_TANK = "SLOP TANK",
	BOILER = "BOILER",
}

export interface IORSMonthWiseData {
	month: number;
	totalQuantityDisposed: number;
}
export interface IORSAggregateRecord {
	disposalMethod: EORSAggregateRecord;
	tenantId: string;
	monthWiseData: IORSMonthWiseData[];
}

export interface IORSVesselSpecificDateRange {
	year: number;
	month: number;
}

export interface IORSVesselSpecificCategory {
	disposalMethod: EORSAggregateRecord;
	totalQuantityDisposed: number;
}

export interface IORBVesselSpecificInfo extends IRawGarbageVesselInfo {
	vesselType: string;
}

export interface IORSVesselSpecificAggregateRecord {
	dateRange: IORSVesselSpecificDateRange;
	disposalMethod: string;
	lastAggregatedAt: string;
	totalQuantityDisposed: number;
	tenantId: string;
	vessel: IORBVesselSpecificInfo;
	_id: string;
}

export interface ICargoSlopMonthWiseRecord {
	month: number;
	totalQuantityDischarged: number;
}

export interface ICargoSlopAggregateRecord {
	tenantId: string;
	monthWiseData: ICargoSlopMonthWiseRecord[];
}

export interface IDateRange {
	month: number;
	year: number;
}

export interface ICargoSlopVesselSpecificAggregateRecord {
	tenantId: string;
	dateRange: IDateRange;
	totalQuantityDischarged: number;
	vessel: IVessel;
	lastAggregatedAt: string;
	_id: string;
}

export interface IOilSpillsInWaterMonthWiseRecord {
	month: number;
	totalQuantityDischarged: number;
}

export interface IOilSpillsInWaterAggregateRecord {
	tenantId: string;
	monthWiseData: IOilSpillsInWaterMonthWiseRecord[];
}

export interface IOilSpillsInWaterVesselSpecificAggregateRecord {
	tenantId: string;
	monthWiseData: IOilSpillsInWaterMonthWiseRecord[];
	vessel: IVessel;
	lastAggregatedAt: string;
	cumulativeQuantityDischarged: number;
}

export interface IComprehensiveNoonReportRecord {
	_id: string;
	name: string;
	vessel: string;
	vesselId: string;
	doc_status: string;
	daysSinceLastReport: number;
	daysSinceVerificationPending: number;
	reportDate: string;
	firstSubmittedAt: string;
	voyage: string;
	status: string;
	condition: string;
	elapsedHours: string;
	distance: string;
	sog: string;
	stw: string;
	rpm: string;
	beaufort: string;
	vlsfoRob: string;
	mgoRob: string;
	ifoRob: string;
	ulsfoRob: string;
	lngRob: string;
	ethaneRob: string;
	lpgbRob: string;
	lpgpRob: string;
	biolngRob: string;
	methanolRob: string;
	bioBlendRob: string;
	reportedConsumption: number;
}

export interface IComprehensiveNoonReportVesselSpecificDataRecord {
	value: string;
	date: string;
	id: string;
}

export interface IComprehensiveNoonReportGraphData {
	graphDataType: string;
	graphMeasurement?: string;
	isBarGraph: boolean;
	isModalVisible: boolean;
}

export interface IComprehensiveNoonReportFilters {
	label: string;
	key: string;
	type: "text" | "number" | "date" | "dropdown";
}

export interface ICNRRangeVesselData {
	data: ICNRRangeVessel[];
	pagination: IPagination;
}

export interface ICNRRangeVesselLocalRange {
	isLocalRange?: boolean;
	metric: string;
	range: number[];
	rangeType?: "WARNING" | "ERROR";
	remarks?: string;
	type: string;
	modifiedAt: string;
}

export interface ICNRRangeVessel {
	vesselId: string;
	vesselName: string;
	localRanges: Record<string, ICNRRangeVesselLocalRange>;
}

export interface ICNRRangeColumn {
	key: string;
	type: string;
	title: string;
}

export interface IReportStore extends IJobHOCState {
	garbage: {
		rawGarbageData: IGarbageRawRecord[] | null;
		vesselId: string;
		year: string;
		allVesselsGarbageDischargeData: IGarbageDischargeAllVesselsRecord[];
		allVesselsGarbageCategoryData: IGarbageCategoryAllVesselsRecord[];
		garbageCustomSearchData: IGarbageCustomSearchRecord[];
	};
	bilgeWater: {
		quarterLabels: string[];
		rawBilgeWaterData: IBilgeWaterRecord[] | null;
		vesselId: string;
		year: string;
		allVesselBilgeWaterData: IBilgeWaterAllVesselAggregateRecord[];
		customSearchData: IBilgeWaterCustomSearchRecord[];
	};
	ors: {
		rawOrsData: IORSAggregateRecord[];
		rawOrsVesselSpecificData: IORSVesselSpecificAggregateRecord[];
		vesselId: string;
		year: string;
		customSearchData: IORSCustomSearchRecord[];
	};
	cargoSlop: {
		rawCargoSlopData: ICargoSlopAggregateRecord[];
		rawCargoSlopVesselSpecificData: ICargoSlopVesselSpecificAggregateRecord[];
		customSearchData: ICargoSlopCustomSearchRecord[];
	};
	oilSpillsInWater: {
		rawOilSpillsInWaterData: IOilSpillsInWaterAggregateRecord[];
		rawOilSpillsInWaterVesselSpecificData: IOilSpillsInWaterVesselSpecificAggregateRecord[];
	};
	comprehensiveNoonReport: {
		rawComprehensiveNoonReportData: IComprehensiveNoonReportRecord[];
		rawComprehensiveNoonReportVesselSpecificData: IComprehensiveNoonReportVesselSpecificDataRecord[];
	};
	cnrRanges: {
		rawCNRRangeVesselsData: ICNRRangeVessel[];
		columns: ICNRRangeColumn[];
		vesselSubType?: string;
		vesselsSelected?: string[];
		reportType?: string;
		baseRangeType: ECNR_BASE_RANGE_TYPE;
		config?: any;
		rangeInfo?: ICNRRangeVesselLocalRange;
		isEditModalVisible: boolean;
		pagination: IPagination;
	};
	navnuc: {
		rawNavnucData: INavnucRecord[];
	};
	packaging: {
		vesselSpecificAggregateData: IPackagingVesselSpecificAggregatedData[];
		allVesselAggregateData: IPackagingAllVesselAggregatedData[];
		customSearchData: IPackagingCustomSearchRecord[];
	};
	emission: {
		vesselSpecificAggregateData: IEmissionsVesselSpecificAggregatedData[];
		allVesselAggregateData: IEmissionsAllVesselAggregatedData[];
		customSearchData: IEmissionsCustomSearchRecord[];
	};
	refrigerant: {
		vesselSpecificAggregateData: IRefrigerantVesselSpecificAggregatedData[];
		allVesselAggregateData: IRefrigerantAllVesselAggregatedData[];
		customSearchData: IRefrigerantCustomSearchRecord[];
	};
	cleaningChemicals: {
		vesselSpecificAggregateData: ICleaningChemicalsVesselSpecificAggregatedData[];
		allVesselAggregateData: ICleaningChemicalsAllVesselAggregatedData[];
		customSearchData: ICleaningChemicalsCustomSearchRecord[];
	};
	emfFleetUsageData: TEMFUsageData[];
	biofouling1FleetData: TBFL1UsageData[];
	biofouling2FleetData: TBFL2UsageData[];
	vesselId: string;
	year: string;
	vesselType: string;
	vesselClass: string;
	filters: Record<string, any>;
	pagination: IPagination;
}

export enum GARBAGE_DISCHARGE_CATEGORIES {
	GARBAGE_DISCHARGED_INTO_SEA = "GARBAGE_DISCHARGED_INTO_SEA",
	GARBAGE_INCINERATED = "GARBAGE_INCINERATED",
	GARBAGE_DISCHARGED_TO_RECEPTION_ASHORE_OR_OTHER_SHIPS = "GARBAGE_DISCHARGED_TO_RECEPTION_ASHORE_OR_OTHER_SHIPS",
}

export enum IRawGarbageDischargeLabel {
	"FOOD_WASTES" = "Food wastes",
}

export interface IRawGarbageDischargeRecord {
	label: string; //Fix me: IRawGarbageDischargeLabel;
	estimatedAmountDischarged: number;
	value: string;
	category: string;
}

export interface IRawGarbageVesselInfo {
	_id: string;
	callSign: string;
	imoNumber: string;
	vesselName: string;
	vesselPrefix: string;
	vesselSubType: string;
	vesselType: string;
}

export interface IRawGarbageDateRange {
	month: number;
	year: number;
}

export interface IRawGarbageCategory {
	category: string; //Fix me - label enum
	garbageDischarged: number;
	label: string; //Fix me - enum
}

export interface IRawGarbageDischargeCategory {
	category: string; //GARBAGE_DISCHARGE_CATEGORIES;
	discharged: number;
	garbageRecords: IRawGarbageDischargeRecord[];
}

export interface IGarbageRawRecord {
	_id: string;
	tenantId: string;
	garbageDischargeCategories: IRawGarbageDischargeCategory[];
	vessel: IRawGarbageVesselInfo;
	totalGarbageDischarged: number;
	lastAggregatedAt: string;
	dateRange: IRawGarbageDateRange;
	garbageCategories: IRawGarbageCategory[];
}

export interface IGarbageCustomSearchVessel {
	imoNumber: string;
	vesselName: string;
}

export interface IGarbageCustomSearchGarbage {
	label: string;
	estimatedAmountDischarged: number;
	value: string;
	category: string;
	remarks: string;
}

export interface IGarbageCustomSearchRecord {
	_id: string;
	entry: string;
	garbage: IGarbageCustomSearchGarbage;
	selectedDate: string;
	state: string;
	tenantId: string;
	vessel: IGarbageCustomSearchVessel;
}

export type IMonthwiseRawGarbageObj = {
	[month: string]: IGarbageRawRecord;
};

export interface IMonthWiseRawGarbageRow {
	[key: string]: Record<string, IRawGarbageDischargeCategory> | Object;
}

// Environmental Report Types

export interface IEmissionsGraphFinalData {
	quarter: string;
	VOC: number;
	NMVOC: number;
}

export interface IEmissionsAllVesselAggregatedData {
	emissionType: string;
	tenantId: string;
	totalEmissionValue: number;
	monthWiseData: {
		month: number;
		emissionValue: number;
	}[];
}

export interface IEmissionsVesselSpecificAggregatedData {
	_id: string;
	tenantId: string;
	totalAggregatedEmissionValue: number;
	emissionTypes: {
		category: string;
		emissionValue: number;
	}[];
	vessel: IVessel;
	lastAggregatedAt: string;
	dateRange: {
		month: number;
		year: number;
	};
}

export interface IRefrigerantCustomSearchVessel {
	imoNumber: string;
	vesselName: string;
}

export interface IRefrigerantCustomSearchRecord {
	_id: string;
	selectedDate: string;
	state: string;
	tenantId: string;
	vessel: IRefrigerantCustomSearchVessel;
	refrigerantType: string;
	refrigerantOtherType: string;
	recordCode: string;
	purchased: number;
	consumed: number;
	leaks: number;
	remarks: string;
	reportingDate: string;
}

export interface IPackagingCustomSearchVessel {
	imoNumber: string;
	vesselName: string;
}

export interface IPackagingCustomSearchRecord {
	_id: string;
	selectedDate: string;
	state: string;
	tenantId: string;
	vessel: IPackagingCustomSearchVessel;
	portName: string;
	supplierName: string;
	packagingStorage: string;
	materialRetainedOnboard: number;
	biodegradable: boolean;
	receivedDate: string;
	remarks: string;
	reportingDate: string;
}

export interface ICleaningChemicalsCustomSearchVessel {
	imoNumber: string;
	vesselName: string;
}

export interface ICleaningChemicalsCustomSearchRecord {
	_id: string;
	selectedDate: string;
	state: string;
	tenantId: string;
	vessel: ICleaningChemicalsCustomSearchVessel;
	category: string;
	section: string;
	substance: string;
	substance_property: string;
	quantity: number;
	units: string;
	remarks: string;
	event: string;
	supplierName: string;
	reportingDate: string;
}

export interface IRefrigerantGraphFinalData {
	quarter: string;
	LEAKED: number;
	PURCHASED: number;
	CONSUMED: number;
}

export interface IRefrigerantAllVesselAggregatedData {
	tenantId: string;
	leakedValue: number;
	purchasedValue: number;
	consumedValue: number;
	monthWiseData: {
		month: number;
		leakedValue: number;
		purchasedValue: number;
		consumedValue: number;
	}[];
}

export interface IRefrigerantVesselSpecificAggregatedData {
	_id: string;
	tenantId: string;
	leakedValue: number;
	purchasedValue: number;
	consumedValue: number;
	vessel: IVessel;
	lastAggregatedAt: string;
	dateRange: {
		month: number;
		year: number;
	};
}

export interface IPackagingGraphFinalData {
	quarter: string;
	BIODEGRADABLE: number;
	NONBIODEGRADABLE: number;
}

export interface IPackagingAllVesselAggregatedData {
	tenantId: string;
	totalAggregatedMaterialROBValue: number;
	packagingType: string;
	monthWiseData: {
		month: number;
		materialROBValue: number;
	}[];
}

export interface IPackagingVesselSpecificAggregatedData {
	_id: string;
	tenantId: string;
	totalAggregatedMaterialROBValue: number;
	packagingTypes: {
		category: string;
		materialROB: number;
	}[];
	vessel: IVessel;
	lastAggregatedAt: string;
	dateRange: {
		month: number;
		year: number;
	};
}

export interface ICleaningChemicalsGraphFinalData {
	quarter: string;
	BIODEGRADABLE: number;
	NONBIODEGRADABLE: number;
}

export interface ICleaningChemicalsAllVesselAggregatedData {
	tenantId: string;
	categoryType: string;
	totalCleaningChemicalsValue: number;
	monthWiseData: {
		month: number;
		cleaningChemicalValue: number;
	}[];
}

export interface ICleaningChemicalsVesselSpecificAggregatedData {
	_id: string;
	tenantId: string;
	totalAccumlatedValue: number;
	categories: {
		category: string;
		accumlatedValue: number;
	}[];
	vessel: IVessel;
	lastAggregatedAt: string;
	dateRange: {
		month: number;
		year: number;
	};
}

export interface IORSCustomSearchVessel {
	imoNumber: string;
	vesselName: string;
}

export interface IORSCustomSearchRecord {
	_id: string;
	selectedDate: string;
	state: string;
	tenantId: string;
	vessel: IORSCustomSearchVessel;
	disposalMethod: string;
	quantityDisposed: number;
}

export interface ICargoSlopCustomSearchVessel {
	imoNumber: string;
	vesselName: string;
}

export interface ICargoSlopCustomSearchRecord {
	_id: string;
	selectedDate: string;
	state: string;
	tenantId: string;
	vessel: ICargoSlopCustomSearchVessel;
	quantityDischarged: number;
}

export interface IEmissionsCustomSearchVessel {
	imoNumber: string;
	vesselName: string;
}

export interface IEmissionsCustomSearchRecord {
	_id: string;
	selectedDate: string;
	state: string;
	tenantId: string;
	vessel: IEmissionsCustomSearchVessel;
	emissionType: string;
	diameterOfReleasePipelineOpening: number;
	lengthOfReleasePipelineFromTank: number;
	releaseDuration: number;
	releaseEndDate: string;
	releaseEndTime: string;
	releaseStartDate: string;
	releaseStartTime: string;
	tankPressureAtStartOfRelease: number;
	tankPressureAtEndOfRelease: number;
	vocNmvoc: number;
}

export interface IBilgeWaterCustomSearchVessel {
	imoNumber: string;
	vesselName: string;
}

export interface IBilgeWaterCustomSearchRecord {
	_id: string;
	selectedDate: string;
	createdAt: string;
	state: string;
	tenantId: string;
	vessel: IBilgeWaterCustomSearchVessel;
	disposalMethod: string;
	quantityDisposed: number;
}

export interface IBilgeWaterRecord {
	_id: string;
	disposalMethod: string;
	tenantId: string;
	totalQuantityDisposed: number;
	vessel: IVessel;
	lastAggregatedAt: string;
	dateRange: {
		month: number;
		year: number;
	};
}

export interface IBilgeWaterAllVesselMonthData {
	month: number;
	totalQuantityDisposed: number;
}

export interface IBilgeWaterAllVesselAggregateRecord {
	disposalMethod: EBilgeWaterType;
	tenantId: string;
	monthWiseData: IBilgeWaterAllVesselMonthData[];
}

export interface ICustomTooltipProps {
	key: string;
	name: string;
	color: string;
}

export enum EDischargeTypes {
	GARBAGE_INCINERATED = "GARBAGE_INCINERATED",
	GARBAGE_DISCHARGED_INTO_SEA = "GARBAGE_DISCHARGED_INTO_SEA",
	GARBAGE_DISCHARGED_TO_RECEPTION_ASHORE_OR_OTHER_SHIPS = "GARBAGE_DISCHARGED_TO_RECEPTION_ASHORE_OR_OTHER_SHIPS",
	ACCIDENTAL_OR_LOSS_OF_GARBAGE_INTO_SEA = "ACCIDENTAL_OR_LOSS_OF_GARBAGE_INTO_SEA",
}

export enum EGarbageCategoryTypes {
	PLASTICS = "A",
	FOOD_WASTES = "B",
	DOMESTIC_WASTE = "C",
	COOKING_OIL = "D",
	INCINERATOR_ASHES = "E",
	OPERATIONAL_WASTES = "F",
	EWASTE = "I",
}

export interface IGarbageDishcargeMonthWiseData {
	month: number;
	amountDischarged: number;
}
export interface IGarbageDischargeAllVesselsRecord {
	dischargeCategory: EDischargeTypes;
	tenantId: string;
	monthWiseData: IGarbageDishcargeMonthWiseData[];
}

export interface IGarbageCategoryMonthWiseData {
	month: number;
	amountDischarged: number;
}
export interface IGarbageCategoryAllVesselsRecord {
	category: EGarbageCategoryTypes;
	label: string;
	tenantId: string;
	monthWiseData: IGarbageCategoryMonthWiseData[];
}

export interface IGarbageCategoryGraphTransformedData {
	name?: string;
	A: number;
	B: number;
	C: number;
	D: number;
	E: number;
	F: number;
	I: number;
}

export enum EBilgeWaterType {
	OWS_OVERBOARD = "OWS Overboard",
	TRANSFER_TO_SLOP = "Transfer to Slop",
}

export interface IBilgeWaterGraphFinalData {
	quarter: string;
	transferToSlop: number;
	owsOverboard: number;
}

export interface IGarbageDischargeGraphFinalData {
	quarter: string;
	disposedAshore: number;
	dischargedToSea: number;
	incinerated: number;
	accidentalRelease: number;
}

export interface IGarbageCategoryGraphFinalData {
	quarter: string;
	A: number;
	B: number;
	C: number;
	D: number;
	E: number;
	F: number;
	I: number;
}

export interface IORSGraphFinalData {
	quarter: string;
	incinerated: number;
	evaporated: number;
	boiler: number;
	transferToSlop: number;
	disposedAshore: number;
	total?: string;
}

export interface IReportCategoryType {
	title: string;
	url?: string;
	isDisabled?: boolean;
	isHidden?: boolean;
}

export interface IReportCategory {
	title: string;
	types: IReportCategoryType[];
	isHidden?: boolean;
}

export interface ICargoSlopGraphFinalData {
	quarter: string;
	totalDischarged: number;
}

export interface IOilSpillsInWaterGraphFinalData {
	quarter: string;
	totalDischarged: number;
}

export interface INavnucRecord {
	_id: string;
	_rev: string;
	modelName: string;
	formName: string;
	data: INavnucFormData;
	createdAt: Date;
	updatedAt: Date;
}
export interface INavnucFormData {
	vesselName: string;
	type_of_report: string;
	date_of_occurrence: string;
	type_of_activity: string;
	possible_consequences: string;
	vessel_draft: number;
	depth_of_water: number;
	name_of_port_allision_point: string;
	description: string;
	name_of_other_vessel: string;
	rank_of_person_related_to_act: string;
	name_of_reporter: string;
	rank_of_reporter: string;
	evidence: INavnucEvidence[];
}
export interface INavnucForms {
	forms: INavnucRecord[];
	pagination: IPagination;
}

export interface INavnucEvidence {
	fileName: string;
	fileType: string;
	fileSize: string;
	attachmentId: string;
	docId: string;
	url: string;
}

export interface IPagination {
	total: number;
	limit: number;
	skip: number;
	hasMore: boolean;
}

export enum ECNR_BASE_RANGE_TYPE {
	LOCAL = "LOCAL_RANGE",
	CONFIG = "CONFIG_RANGE",
}

export interface ICNRCellInfo {
	key: string;
	metric: string;
	type: string;
}

export interface ILoadCNRRangeVesselListParams {
	limit: number;
	skip: number;
	baseRangeType: ECNR_BASE_RANGE_TYPE;
	action: string;
	kind: string;
	vesselIds?: string;
}

export interface ICNRRangesVesselsLoadParams {
	baseRangeType: ECNR_BASE_RANGE_TYPE;
	selectedVessels?: string[];
	paginationParams?: IPagination;
}

export interface ICellRangeUpdatePayload {
	vesselId: string;
	metricKey: string;
	metricTitle: string;
	kind: "cnr" | string;
	newRange: number[];
	rangeType: "ERROR" | "WARNING";
	remarks: string;
	type: string;
}
